import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "..";

const fontSizeClasses = {
  xs: "text-[12px]",
  "xs-sm": "text-[14px]",
  sm: "text-[16px]",
  md: "text-[18px]",
  lg: "text-[20px]",
  xl: "text-[22px]",
};

const borderSizeClasses = {
  xs: "border-2",
  "xs-sm": "border-2",
  sm: "border-[3px]",
  md: "border-2",
  lg: "border-2",
  xl: "border-4",
};

const borderColorClasses = {
  contained: {
    primary: "border-primary-700",
    warning: "border-warning-700",
    success: "border-success-700",
    danger: "border-danger-700",
    black: "border-gray-200",
    white: "border-gray-700",
  },
  outlined: {
    primary: "border-primary-200",
    warning: "border-warning-200",
    success: "border-success-200",
    danger: "border-danger-200",
    black: "border-gray-200",
    white: "border-gray-300",
  },
  text: {
    primary: "border-primary-200",
    warning: "border-warning-200",
    success: "border-success-200",
    danger: "border-danger-200",
    black: "border-gray-300",
    white: "border-gray-300",
  },
};

const tooltipVariants = {
  contained: {
    primary: "bg-primary text-white",
    warning: "bg-warning-500 text-white",
    success: "bg-success-500 text-white",
    danger: "bg-danger-500 text-white",
    black: "bg-black text-white",
    white: "bg-white text-black",
  },
  text: {
    primary: "bg-white text-primary",
    warning: "bg-white text-warning-500",
    success: "bg-white text-success-500",
    danger: "bg-white text-danger-500",
    black: "bg-white text-black",
    white: "bg-black text-white",
  },
  outlined: {
    primary: "bg-primary-50  text-primary",
    warning: "bg-warning-50  text-warning-500",
    success: "bg-success-50  text-success-500",
    danger: "bg-danger-50  text-danger-500",
    black: "bg-gray-100  text-black",
    white: "bg-gray-500  text-white",
  },
};

const SmartTooltip = ({
  children,
  description,
  size = "xs", // xs, sm, md, lg, xl
  color = "black", // primary, warning, success, danger, black, white
  tooltipPosition = "right", // top, bottom, left, right
  variant = "contained", // outlined, text
  delay = 0,
  offset = 18,
  noicon = false,
  noTooltip = false,
  portal = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(tooltipPosition);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const childRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setVisible(true);
      hoverTimeoutRef.current = null;
    }, delay);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setVisible(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    const tooltip = tooltipRef.current;
    const child = childRef.current;
    if (!tooltip || !child) return;

    const tooltipRect = tooltip.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    let newPosition = tooltipPosition;

    if (
      tooltipPosition === "right" &&
      childRect.right + tooltipRect.width + offset > screenWidth
    ) {
      newPosition = "left";
    } else if (
      tooltipPosition === "left" &&
      childRect.left - tooltipRect.width - offset < 0
    ) {
      newPosition = "right";
    } else if (
      tooltipPosition === "top" &&
      childRect.top - tooltipRect.height - offset < 0
    ) {
      newPosition = "bottom";
    } else if (
      tooltipPosition === "bottom" &&
      childRect.bottom + tooltipRect.height + offset > screenHeight
    ) {
      newPosition = "top";
    }

    setPosition(newPosition);
  }, [visible, tooltipPosition, offset]);

  useEffect(() => {
    if (!visible || !portal) return;

    const compute = () => {
      const tooltip = tooltipRef.current;
      const child = childRef.current;
      if (!tooltip || !child) return;

      const tooltipRect = tooltip.getBoundingClientRect();
      const childRect = child.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      const pad = 8;

      const getCoords = (pos) => {
        if (pos === "top") {
          return {
            top: childRect.top - tooltipRect.height - offset,
            left: childRect.left + childRect.width / 2 - tooltipRect.width / 2,
          };
        }
        if (pos === "bottom") {
          return {
            top: childRect.bottom + offset,
            left: childRect.left + childRect.width / 2 - tooltipRect.width / 2,
          };
        }
        if (pos === "left") {
          return {
            top: childRect.top + childRect.height / 2 - tooltipRect.height / 2,
            left: childRect.left - tooltipRect.width - offset,
          };
        }
        return {
          top: childRect.top + childRect.height / 2 - tooltipRect.height / 2,
          left: childRect.right + offset,
        };
      };

      let { top, left } = getCoords(position);

      top = Math.max(
        pad,
        Math.min(top, screenHeight - tooltipRect.height - pad),
      );
      left = Math.max(
        pad,
        Math.min(left, screenWidth - tooltipRect.width - pad),
      );

      setCoords({ top, left });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [visible, portal, position, offset]);

  return (
    <div className={`flex items-center gap-1`}>
      {!noicon && children}
      <div className="relative inline-block" ref={childRef}>
        {noicon ? (
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {children}
          </div>
        ) : (
          <Button
            type="icon"
            size={size === "lg" || size === "xl" ? size : "md"}
            icon={"info"}
            color={color}
            variant="text"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            disabled={noTooltip}
          />
        )}
        {portal ? (
          createPortal(
            <AnimatePresence>
              {visible && !noTooltip && (
                <motion.div
                  ref={tooltipRef}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className={`fixed whitespace-nowrap px-4 py-3 rounded-md z-[9999] leading-none
            ${tooltipVariants[variant][color]}
            ${fontSizeClasses[size]}
            ${borderSizeClasses[size]}
            ${borderColorClasses[variant][color]}
          `}
                  style={{
                    top: coords.top,
                    left: coords.left,
                  }}
                >
                  {description}
                </motion.div>
              )}
            </AnimatePresence>,
            document.body,
          )
        ) : (
          <AnimatePresence>
            {visible && !noTooltip && (
              <motion.div
                ref={tooltipRef}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className={`absolute whitespace-nowrap px-4 py-3 rounded-md z-50  leading-none
            ${tooltipVariants[variant][color]}
            ${fontSizeClasses[size]}
            ${borderSizeClasses[size]}
            ${borderColorClasses[variant][color]}
            ${
              position === "top"
                ? "bottom-full -left-1/3 transform -translate-x-1/3 mb-2"
                : ""
            }
            ${
              position === "bottom"
                ? "top-full -left-1/3 transform -translate-x-1/3 mt-2"
                : ""
            }
            ${
              position === "left"
                ? "right-full top-1/2 transform -translate-y-1/2 -translate-x-2 mr-2"
                : ""
            }
              ${
                position === "right"
                  ? "left-full top-1/2 transform -translate-y-1/2 translate-x-2 ml-2"
                  : ""
              }
          `}
              >
                {description}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SmartTooltip;
